import React from 'react';

/**
 * TrustpilotStars Component
 * Renders Trustpilot-style green filled star ratings as an inline SVG.
 * Replaces all previous lucide Star icon ratings across the site.
 */

interface TrustpilotStarsProps {
  /** Number of filled stars (1-5) */
  count?: number;
  /** Height class for sizing (e.g., "h-4", "h-5", "h-6") */
  className?: string;
}

export default function TrustpilotStars({ count = 5, className = "h-4" }: TrustpilotStarsProps) {
  // Each star is 96px wide with 8px gap between them in the viewBox
  // Total viewBox: 5 stars * 96px + 4 gaps * 8px = 512px wide, 96px tall
  const totalWidth = count * 96 + (count - 1) * 8;
  
  // Generate star background rectangles and star path shapes
  const starPaths: React.ReactElement[] = [];
  
  for (let i = 0; i < count; i++) {
    const x = i * 104; // 96px star + 8px gap
    const cx = x + 48; // center of each star
    
    // Green background rectangle
    starPaths.push(
      <rect key={`bg-${i}`} x={x} y={0} width={96} height={96} fill="#00b67a" />
    );
    
    // White star shape (Trustpilot style)
    starPaths.push(
      <path
        key={`star-${i}`}
        fill="#ffffff"
        d={`M${cx} 64.7L${cx + 14.6} 61l6.1 18.8zM${cx + 33.6} 40.4h-25.7L${cx} 16.2l-7.9 24.2h-25.7l20.8 15-7.9 24.2 20.8-15 12.8-9.2z`}
      />
    );
  }
  
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${totalWidth} 96`}
      className={`${className} w-auto inline-block`}
      aria-label={`${count} out of 5 stars`}
      role="img"
    >
      {starPaths}
    </svg>
  );
}
